import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Step5Props {
  onNext: (data: Step5Data) => void;
  onBack: () => void;
  initialData?: Step5Data;
}

export interface Step5Data {
  expectedUsers: string;
  userRoles?: string;
}

const userScales = [
  { value: '1-100', label: '1-100 users', description: 'Small team or personal use' },
  { value: '100-1k', label: '100-1,000 users', description: 'Growing business' },
  { value: '1k-10k', label: '1,000-10,000 users', description: 'Established business' },
  { value: '10k+', label: '10,000+ users', description: 'Enterprise scale' },
];

export const Step5UsersScale = ({ onNext, onBack, initialData }: Step5Props) => {
  const [expectedUsers, setExpectedUsers] = useState(initialData?.expectedUsers || '');
  const [userRoles, setUserRoles] = useState(initialData?.userRoles || '');

  const handleSubmit = () => {
    if (expectedUsers) {
      onNext({ expectedUsers, userRoles });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Users & Scale</h2>
        <p className="text-gray-600 mt-2">Help us understand the scale of your application</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Expected Users *</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {userScales.map((scale) => (
            <div
              key={scale.value}
              onClick={() => setExpectedUsers(scale.value)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                expectedUsers === scale.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <h4 className="font-semibold text-gray-900">{scale.label}</h4>
              <p className="text-sm text-gray-600">{scale.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="roles" className="block text-sm font-medium text-gray-700 mb-2">
          User Roles Needed (Optional)
        </label>
        <Input
          id="roles"
          type="text"
          value={userRoles}
          onChange={(e) => setUserRoles(e.target.value)}
          placeholder="e.g., Admin, Customer, Manager, Staff"
        />
        <p className="text-sm text-gray-500 mt-1">
          Different types of users with different permissions
        </p>
      </div>

      <div className="flex justify-between">
        <Button onClick={onBack} variant="outline" className="px-8">
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!expectedUsers}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
